import { useMemo } from 'react'

import type { TProductsList } from '@/contexts/PlanContext/type'
import type { TUseSubLog } from '@/contexts/SiteSpecificContext/hooks/subLog'

import { getUserTagByUserGroup } from '@/hooks/userGroup'
import { UserTag } from '@/hooks/userGroup/type'
import { useSiteSpecificContext } from '@/contexts/SiteSpecificContext'

export enum EPricingExperimentVariant {
  A = 'A',
  B = 'B',
}

// 未订阅用户统一回退旧版 B 组时，使用旧分桶起始值作为代表 group，
// 让下游商品过滤稳定命中旧 B 组 SKU，而不再跟随当前用户自身分桶。
const LEGACY_PRICING_B_VARIANT_USER_GROUP = '131'

type TCurrentCreditsProduct = NonNullable<
  TUseSubLog['creditsPlan']
>['productsSub']

interface IPricingExperimentInfo {
  variant: EPricingExperimentVariant
  effectiveUserGroup?: string
}

function getVariantByUserTag(userTag?: UserTag) {
  if (userTag === UserTag.A || userTag === UserTag.B || userTag === UserTag.C) {
    return EPricingExperimentVariant.A
  }

  return EPricingExperimentVariant.B
}

function getVariantByProductUserGroup(userGroup?: string) {
  if (!userGroup) {
    return undefined
  }

  const userGroupRanges = userGroup.split(',')
  const variants = userGroupRanges
    .map((range) => {
      const [start, end = start] = range.split('-').map(Number)

      if (!Number.isFinite(start) || !Number.isFinite(end)) {
        return undefined
      }

      const startVariant = getVariantByUserTag(
        getUserTagByUserGroup(`${start}`),
      )
      const endVariant = getVariantByUserTag(getUserTagByUserGroup(`${end}`))

      if (startVariant !== endVariant) {
        return undefined
      }

      return startVariant
    })
    .filter((item): item is EPricingExperimentVariant => !!item)

  if (!variants.length) {
    return undefined
  }

  return variants.every((variant) => variant === variants[0])
    ? variants[0]
    : undefined
}

function matchCurrentPlanProduct({
  currentProduct,
  productList,
}: {
  currentProduct?: TCurrentCreditsProduct
  productList: TProductsList
}) {
  if (!currentProduct) {
    return undefined
  }

  // 已订阅用户优先按稳定标识匹配当前 SKU，只有前两者都缺失时才退回到名称+配额的弱匹配。
  return (
    productList.find(
      (product) => product.productId === currentProduct.productId,
    ) ||
    productList.find((product) => product.priceId === currentProduct.priceId) ||
    productList.find((product) => {
      return (
        product.productName === currentProduct.productName &&
        product.usageType === currentProduct.usageType &&
        product.usageCount === currentProduct.usageCount &&
        product.duration === currentProduct.duration
      )
    })
  )
}

function getEffectiveUserGroupByProductUserGroup(userGroup?: string) {
  if (!userGroup) {
    return undefined
  }

  // 这里不需要完整区间，只需要一个能落入该商品分桶的代表 userGroup，
  // 供后续商品过滤复用现有区间判断即可。
  const [firstRange = ''] = userGroup.split(',')
  const [start] = firstRange.split('-')

  return start || undefined
}

export function getPricingExperimentInfo({
  userTag,
  userGroup,
  hasActivePlan,
  currentProduct,
  productList,
}: {
  userTag?: UserTag
  userGroup?: string
  hasActivePlan?: boolean
  currentProduct?: TCurrentCreditsProduct
  productList: TProductsList
}): IPricingExperimentInfo {
  if (hasActivePlan && currentProduct) {
    // 仍在有效期内的已订阅用户优先跟随“当前订阅商品所属分桶”决定 A/B，
    // 这样取消但未过期的用户，看到的套餐集合仍和自己当前权益保持一致。
    const matchedProduct = matchCurrentPlanProduct({
      currentProduct,
      productList,
    })
    const matchedVariant = getVariantByProductUserGroup(
      matchedProduct?.userGroup,
    )
    const effectiveUserGroup = getEffectiveUserGroupByProductUserGroup(
      matchedProduct?.userGroup,
    )

    if (matchedVariant) {
      return {
        variant: matchedVariant,
        effectiveUserGroup,
      }
    }

    // 跨端已订阅但当前 SKU 无法映射到 web 商品列表时，
    // 回退到用户自身分流，避免因匹配失败被强制塞进 B 组。
    return {
      variant: getVariantByUserTag(userTag),
      effectiveUserGroup: userGroup,
    }
  }

  return {
    // variant: getVariantByUserTag(userTag),
    // effectiveUserGroup: userGroup,
    variant: EPricingExperimentVariant.B,
    effectiveUserGroup: LEGACY_PRICING_B_VARIANT_USER_GROUP,
  }
}

export function usePricingExperimentInfo(productList: TProductsList) {
  const { user, plan } = useSiteSpecificContext()

  const currentProduct = plan.creditsPlan?.productsSub

  return useMemo(() => {
    return getPricingExperimentInfo({
      userTag: user.userTag,
      userGroup: user.userGroup,
      hasActivePlan: plan.hasActivePlan,
      currentProduct,
      productList,
    })
  }, [
    currentProduct,
    plan.hasActivePlan,
    productList,
    user.userGroup,
    user.userTag,
  ])
}
